import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<AggregationElectionView />` — patient-elected aggregation across
 * holders.
 *
 * Phase A scaffolding per design memo PR #150. Drives the
 * `IAggregationElectionFlow` orchestrator + dispatches step views by
 * current state. Substrate-event-triggered cadence (per BMA v4.0
 * §3.5.2 phased rollout) is cohort-gated per ADR-030; client checks
 * `fetchHolderCohortFlags` and surfaces the option only when enabled.
 *
 * **Doctrinal posture (encoded structurally):**
 *   - Patient-rights authority frame, NOT Patient-Directed Use
 *     Authority + Consent Artifact (memo §1.2): aggregation does
 *     NOT issue Consent Artifacts; election state is patient-
 *     fiduciary metadata not consent material.
 *   - ADR-026 §3 non-delegable fiduciary authority.
 *   - Pattern B aggregation-payload posture (Issue #51): records
 *     flow into PR #123 patient-projection rendering pipeline as
 *     `aggregated-payload` content category.
 *   - Revocation ≠ erasure (ADR-024 §2.7.5): revocation surface
 *     surfaces honest "revoke-future-aggregation ≠ erase-prior-content"
 *     framing in the active-aggregations management view (separate;
 *     subsequent PR).
 *
 * **Patient-Facing Terms v1.3 doctrinal-copy gate** (coord issue #42).
 */
import * as React from 'react';
import { useLocale } from '../i18n/locale-context.js';
import { AggregationCancelledOrErrorStep } from './_aggregation/cancelled-or-error-step.js';
import { AggregationElectionCompleteStep } from './_aggregation/election-complete-step.js';
import { AggregationElectionConfirmationStep } from './_aggregation/election-confirmation-step.js';
import { AggregationElectionExecutingStep } from './_aggregation/election-executing-step.js';
import { AggregationIntroStep } from './_aggregation/intro-step.js';
import { HolderInventoryStep } from './_aggregation/holder-inventory-step.js';
import { PerHolderConfigurationStep } from './_aggregation/per-holder-configuration-step.js';
function useAggregationFlowState(flow) {
    const [state, setState] = React.useState(() => flow.getState());
    React.useEffect(() => {
        return flow.subscribe(setState);
    }, [flow]);
    return state;
}
export function AggregationElectionView(props) {
    const { t } = useLocale();
    if (props.flow === undefined) {
        return (_jsxs("section", { "aria-labelledby": "aggregation-heading", children: [_jsx("h2", { id: "aggregation-heading", children: t('aggregation.heading') }), _jsx("p", { children: t('aggregation.body') }), _jsx("p", { children: _jsx("em", { children: t('aggregation.unwired_notice') }) })] }));
    }
    return (_jsx(AggregationElectionViewWired, { flow: props.flow, ...(props.onRestartRequested !== undefined
            ? { onRestartRequested: props.onRestartRequested }
            : {}) }));
}
function AggregationElectionViewWired(props) {
    const { t } = useLocale();
    const state = useAggregationFlowState(props.flow);
    React.useEffect(() => {
        if (state.kind === 'intro') {
            void props.flow.loadInventory();
        }
        // Only fire on initial mount + on subsequent intro re-entries.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.flow]);
    const handleRestart = () => {
        if (props.onRestartRequested !== undefined) {
            props.onRestartRequested();
        }
    };
    return (_jsxs("section", { "aria-labelledby": "aggregation-heading", children: [_jsx("h2", { id: "aggregation-heading", children: t('aggregation.heading') }), renderStep(state, props.flow, handleRestart)] }));
}
function renderStep(state, flow, handleRestart) {
    switch (state.kind) {
        case 'intro':
            return (_jsx(AggregationIntroStep, { onBegin: () => {
                    // Flow already loading inventory via the mount effect; transitions
                    // automatically to holder-inventory once load completes.
                }, onCancel: () => flow.cancel('user-cancelled-at-intro') }));
        case 'holder-inventory':
            return (_jsx(HolderInventoryStep, { inventory: state.inventory, onContinue: (holderIds) => flow.selectHolders(holderIds), onCancel: () => flow.cancel('user-cancelled-at-inventory') }));
        case 'holder-selection':
            return (_jsx(HolderInventoryStep, { inventory: state.inventory, onContinue: (holderIds) => {
                    flow.selectHolders(holderIds);
                    flow.beginPerHolderConfiguration();
                }, onCancel: () => flow.cancel('user-cancelled-at-selection') }));
        case 'per-holder-configuration': {
            // Phase A simplification: configure holders in selection order.
            // The currentHolderIndex tracks position; the parent view picks
            // the holder from the inventory list. When the index exceeds
            // available holders, flow advances to confirmation via
            // beginConfirmation() called from the orchestrator integration.
            const selected = state.inventory.filter((h) => state.configs.every((c) => c.holderId !== h.holderId));
            const currentHolder = selected[0];
            if (currentHolder === undefined) {
                // All holders configured; transition to confirmation
                flow.beginConfirmation();
                return _jsx(AggregationElectionExecutingStep, {});
            }
            return (_jsx(PerHolderConfigurationStep, { holder: currentHolder, 
                // Phase A scaffolding: cohort-flag check stubbed false; Phase B
                // wiring fetches per-holder cohort flags via
                // client.fetchHolderCohortFlags()
                substrateEventTriggeredEnabled: false, onConfigure: (opts) => flow.configureHolder({
                    holderId: currentHolder.holderId,
                    dataCategories: opts.dataCategories,
                    cadence: opts.cadence,
                    retention: opts.retention,
                }), onCancel: () => flow.cancel('user-cancelled-at-per-holder-configuration') }));
        }
        case 'election-confirmation':
            return (_jsx(AggregationElectionConfirmationStep, { configs: state.configs, onConfirm: () => {
                    void flow.confirmElection();
                }, onCancel: () => flow.cancel('user-cancelled-at-confirmation') }));
        case 'election-executing':
            return _jsx(AggregationElectionExecutingStep, {});
        case 'election-complete':
            return (_jsx(AggregationElectionCompleteStep, { configs: state.configs, initiatedFirstPullAt: state.initiatedFirstPullAt, onClose: handleRestart }));
        case 'cancelled':
            return (_jsx(AggregationCancelledOrErrorStep, { kind: "cancelled", detail: state.reason, onRestart: handleRestart }));
        case 'error':
            return (_jsx(AggregationCancelledOrErrorStep, { kind: "error", detail: `${state.code}: ${state.message}`, onRestart: handleRestart }));
    }
}
//# sourceMappingURL=aggregation-election-view.js.map